import { Component, signal, computed, inject, OnInit, input, output, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ProfessionalsDataService } from '../../../services/professionals-data.service';
import { ProfessionalsWizardService } from '../../../services/professionals-wizard.service';
import { ProfessionalsService } from '../../../../core/services/professionals.service';
import { QuestionnaireService } from '../../../../core/services/questionnaire.service';
import { QuestionnairePdfHeaderData } from '../../../../core/services/questionnaire.service';
import { FileChecklistStatusDTO } from '../../../../core/models/file-checklist.model';
import { FileValidationService } from '../../../../core/services/file-validation.service';
import { RegistrationStepService, RegistrationStepResponse, RegistrationStatus, RegistrationStep } from '../../../../core/services/registration-step.service';
import { ProfessionalOperationalDetailsService } from '../../../../backoffice/services/axa-professional-data.service';

import { ActivatedRoute } from '@angular/router';
import { Alert, getAlertStatus } from '../../../../core/models/models';
import { TooltipModule } from 'primeng/tooltip';
import { addSolCodeToFileName } from '../../../../core/utils/file-name.utils';


interface AttachmentItem {
  id: string;
  title: string;
  fileName: string;
  fileSize: string;
  // Cambia uploadDate o expirationDate per accettare Date
  uploadDate: string | null;
  expirationDate?: Date | null; // <--- ASSICURATI CHE SIA Date | null
  isMandatory: boolean;
  isUploaded: boolean;
  templateUrl?: string | null;
  isChecklistItem?: boolean; // true = checklist item, template si scarica via API
  fileSystemPath?: string | null; // URL S3 del file caricato dall'utente
  file?: File | null;
  // Campi del collega
  allowedExtensionsRaw?: string[];
  allowedExtensions?: string[];
  hasExpirationDate?: boolean;
  pendingFile?: File | null;
}

interface AttachmentViewItem extends AttachmentItem {
  sectionId: string;
  answerId?: string | null; // Cambiato in | null
  instanceId?: string | null; // Cambiato in | null
}

interface AttachmentSection {
  id: string;
  title: string;
  items: AttachmentItem[];
}

@Component({
  selector: 'app-professionals-questionnaire-attachments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DatePickerModule,
    FileUploadModule,
    DialogModule,
    ToastModule,
    TooltipModule,
  ],
  providers: [MessageService],
  templateUrl: './professionals-questionnaire-attachments.component.html',
  styleUrl: './professionals-questionnaire-attachments.component.scss',
})
export class ProfessionalsQuestionnaireAttachmentsComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private professionalsDataService = inject(ProfessionalsDataService);
  
  // Optional professional ID from parent component (for review page)
  professionalIdInput = input<string | null>(null);
  
  // Optional input to force readonly mode (for review/detail pages)
  readonly = input<boolean>(false);

  // Optional input to force editable mode (for BO data entry on NET- body shops)
  forceEditable = input<boolean>(false);

  // When true, hides the questionnaires section (VRF + generic) — used for network body shop onboarding
  hideQuestionnaires = input<boolean>(false);

  // Alerts relativi al professionista (passati da review-professional per mostrare badge)
  alerts = input<Alert[]>([]);

  /** Ritorna gli alert attivi per un questionario, con fallback sul nome per i casi legacy */
  getQuestionnaireAlerts(questionnaire: Pick<AttachmentViewItem, 'id' | 'title'>): Alert[] {
    const questionnaireId = this.normalizeQuestionnaireKey(questionnaire.id);
    const questionnaireTitle = this.normalizeQuestionnaireKey(questionnaire.title);

    return this.alerts().filter((alert) => {
      if (getAlertStatus(alert) === 'RESOLVED') {
        return false;
      }

      const alertQuestionnaireId = this.normalizeQuestionnaireKey(alert.questionnaireId);
      if (questionnaireId && alertQuestionnaireId === questionnaireId) {
        return true;
      }

      const alertQuestionnaireName = this.normalizeQuestionnaireKey(alert.questionnaireName);
      if (!questionnaireTitle || !alertQuestionnaireName) {
        return false;
      }

      return (
        alertQuestionnaireName === questionnaireTitle ||
        alertQuestionnaireName.includes(questionnaireTitle) ||
        questionnaireTitle.includes(alertQuestionnaireName)
      );
    });
  }

  private normalizeQuestionnaireKey(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }

  // Emits true when all mandatory checklist items are uploaded
  allMandatoryChecklistCompleted = output<boolean>();
  
  protected professionalsWizardService = inject(ProfessionalsWizardService);
  private professionalsService = inject(ProfessionalsService);
  private questionnaireService = inject(QuestionnaireService);
  private professionalOperationalDetailsService = inject(ProfessionalOperationalDetailsService);
  private registrationStepService = inject(RegistrationStepService);
  private messageService = inject(MessageService);
  private fileValidationService = inject(FileValidationService);

  isSubmitting = signal<boolean>(false);

  // Getter reattivi: leggono il valore aggiornato dal servizio ad ogni accesso,
  // evitando il bug in review mode dove il servizio viene popolato in modo asincrono
  // dopo che il componente è già montato.
  get tipologiaFiduciario() { return this.professionalsDataService.getTipologiaFiduciario(); }
  get tipologiaCarrozzeria() { return this.professionalsDataService.getTipologiaCarrozzeria(); }

  // Live region message for screen readers
  liveRegionMessage = signal<string>('');

  // Current registration step (for detecting modification requests)
  currentRegistrationStep = signal<RegistrationStepResponse | null>(null);
  
  // Step history (to check for MODIFICATION_REQUESTED)
  stepHistory = signal<RegistrationStepResponse[]>([]);
  
  // VRF questionnaire status
  vrfStatus = signal<'DRAFT' | 'FILLED' | null>(null);
  solCode = signal<string | null>(null);

  // Computed: Check if there's a modification request
  hasModificationRequest = computed(() => {
    const step = this.currentRegistrationStep();
    const history = this.stepHistory();
    
    if (!step || this.professionalIdInput()) return false; // Don't show in review mode
    
    // Only show banner if:
    // 1. Current step is DATA_INPUT with USER_INPUT status
    // 2. There's a VERIFICATION step with MODIFICATION_REQUESTED in history
    if (step.step === 'DATA_INPUT' && step.status === RegistrationStatus.USER_INPUT) {
      return history.some(s => 
        s.step === 'VERIFICATION' && 
        s.status === RegistrationStatus.MODIFICATION_REQUESTED
      );
    }
    
    return false;
  });
  
  // Computed: Check if all fields should be disabled (readonly mode or review mode)
  isFieldsDisabled = computed(() => {
    if (this.forceEditable()) return false;
    return this.readonly() || this.professionalIdInput() !== null;
  });

  // Computed: Get modification comment from VERIFICATION MODIFICATION_REQUESTED step
  modificationComment = computed(() => {
    const history = this.stepHistory();
    
    // Find the most recent VERIFICATION with MODIFICATION_REQUESTED
    const modificationSteps = history
      .filter(s => s.step === 'VERIFICATION' && s.status === RegistrationStatus.MODIFICATION_REQUESTED)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    if (modificationSteps.length > 0) {
      // Parse "Modification requested: actual message" format
      const comment = modificationSteps[0].comments || '';
      return comment.replace(/^Modification requested:\s*/, '').trim();
    }
    
    return null;
  });

  // Signal for expand/collapse state of modification notes
  isModificationNotesExpanded = signal<boolean>(false);
  private readonly MAX_COMMENT_LENGTH = 150;

  // Computed: Check if comment is long
  isCommentLong = computed(() => {
    const comment = this.modificationComment();
    return comment ? comment.length > this.MAX_COMMENT_LENGTH : false;
  });

  // Computed: Get truncated comment
  displayedComment = computed(() => {
    const comment = this.modificationComment();
    if (!comment) return null;
    
    if (this.isCommentLong() && !this.isModificationNotesExpanded()) {
      return comment.substring(0, this.MAX_COMMENT_LENGTH) + '...';
    }
    
    return comment;
  });

  // Toggle modification notes expansion
  toggleModificationNotes(): void {
    this.isModificationNotesExpanded.update(expanded => !expanded);
  }

  // Computed: verifica se lo step corrente è completato
  isStepCompleted = computed(() => {
    const currentStep = this.professionalsWizardService.steps().find((step) => step.id === 2);
    return currentStep?.completed ?? false;
  });

  // Computed: verifica se siamo in modalità read-only
  isReadOnly = computed(() => {
    if (this.forceEditable()) return false;
    return this.professionalsDataService.isReadOnly() || this.readonly() || this.professionalIdInput() !== null;
  });

  professionalSummaryBusinessName = computed(() => {
    const generalData = this.professionalsWizardService.cachedGeneralData();
    return generalData?.companyData?.businessName || generalData?.businessName || this.businessNameFromApi();
  });

  private businessNameFromApi = signal<string>('');
  private professionalTypeFromApi = signal<string>('');

  professionalIdDisplay = computed(() => {
    return this.professionalIdInput() || this.getCurrentProfessionalId() || '';
  });

  professionalSummaryType = computed(() => {
    return this.tipologiaLabel();
  });

  // Computed: nasconde la sezione questionari — sia quando passato via input, sia in modalità network onboarding
  shouldHideQuestionnaires = computed(() =>
    this.hideQuestionnaires() || this.professionalsWizardService.isNetworkOnboarding()
  );

  // Computed: determina se mostrare il Questionario VRF
  // Nascosto solo per carrozzerie AXA Deutsche Bank
  shouldShowVRFQuestionnaire = computed(() => {
    // VRF è obbligatorio per tutti TRANNE le carrozzerie AXA Deutsche Bank
    return this.tipologiaCarrozzeria !== 'AXA_DEUTSCHE_BANK';
  });

  // Success and error messages removed - using MessageService instead

  // Questionnaires and attachments sections
  sections = signal<AttachmentSection[]>([]);
  checklistSectionName = signal<string>('Checklist');
  showChecklistUploadDialog = signal<boolean>(false);
  uploadTargetSectionId = signal<string | null>(null);
  uploadTargetItemId = signal<string | null>(null);
  uploadValidationAttempted = signal<boolean>(false);
  uploadInlineErrorMessage = signal<string>('');
  savingExpirationFor = signal<string | null>(null);
  dirtyExpirationItems = signal<Set<string>>(new Set());

  // Getter/setter necessario per two-way binding con p-dialog
  get showChecklistUploadDialogValue(): boolean {
    return this.showChecklistUploadDialog();
  }

  set showChecklistUploadDialogValue(value: boolean) {
    this.showChecklistUploadDialog.set(value);
  }

  tipologiaLabel = computed(() => {
    const map: Record<string, string> = {
      AUTO_EXPERT: 'Perito Auto',
      RE_EXPERT: 'Perito RE',
      INVESTIGATOR: 'Investigatore',
      DOCTOR: 'Medico Legale',
      LAWYER: 'Legale',
      BODYSHOP: 'Carrozzeria',
      NETWORK: 'Fornitore Rete',
      HAIL_PROVIDER: 'Fornitore Grandine',
    };
    const cachedType = this.professionalsWizardService.cachedGeneralData()?.professionalType;
    const tipologia = (this.professionalTypeFromApi() || cachedType || this.tipologiaFiduciario || '').trim().toUpperCase();
    return map[tipologia] || tipologia;
  });

  questionnaireItems = computed<AttachmentViewItem[]>(() => {
    return this.sections()
      .filter((section) => section.title.toLowerCase().includes('questionari'))
      .flatMap((section) =>
        section.items.map((item) => ({
          ...item,
          sectionId: section.id,
        })),
      );
  });

  checklistItems = computed<AttachmentViewItem[]>(() => {
    return this.sections()
      .filter((section) => !section.title.toLowerCase().includes('questionari'))
      .flatMap((section) =>
        section.items.map((item) => ({
          ...item,
          sectionId: section.id,
        })),
      );
  });

  // Computed: verifica se tutti i questionari sono completati
  // Restituisce true se non ci sono questionari (nulla da completare), simmetrico con areChecklistsCompleted
  areQuestionnairesCompleted = computed(() => {
    const items = this.questionnaireItems();
    if (items.length === 0) return true; // Nessun questionario = completato
    return items.every(item => item.isUploaded);
  });

  // Computed: verifica se tutte le checklist sono completate
  // Ritorna true se non ci sono checklist (nulla da completare)
  areChecklistsCompleted = computed(() => {
    const items = this.checklistItems();
    if (items.length === 0) return true; // Nessuna checklist = completato
    return items.every(item => item.isUploaded || !item.isMandatory); // Solo le obbligatorie devono essere caricate
  });

  // Computed: verifica se lo step dovrebbe essere completato in base ai dati locali
  shouldStepBeCompleted = computed(() => {
    // Per NK_BODYSHOP il completamento dipende solo dagli allegati (no questionari)
    if (this.professionalsWizardService.isNetworkOnboarding()) {
      return this.checklistItems().length > 0 && this.areChecklistsCompleted();
    }
    // Tutti i questionari obbligatori devono essere completati
    const questionnairesOk = this.areQuestionnairesCompleted();
    // Tutte le checklist obbligatorie devono essere completate (o non ci sono checklist)
    const checklistsOk = this.areChecklistsCompleted();

    const hasAttachmentRequirements =
      this.questionnaireItems().length > 0 || this.checklistItems().length > 0;
    return hasAttachmentRequirements && questionnairesOk && checklistsOk;
  });

  activeChecklistUploadItem = computed<AttachmentViewItem | null>(() => {
    const sectionId = this.uploadTargetSectionId();
    const itemId = this.uploadTargetItemId();

    if (!sectionId || !itemId) {
      return null;
    }

    const section = this.sections().find((entry) => entry.id === sectionId);
    const item = section?.items.find((entry) => entry.id === itemId);

    if (!section || !item) {
      return null;
    }

    return {
      ...item,
      sectionId,
    };
  });

  // Compute if all mandatory items are uploaded
  allMandatoryCompleted = computed(() => {
    const allSections = this.sections();
    for (const section of allSections) {
      for (const item of section.items) {
        if (item.isMandatory && !item.isUploaded) {
          return false;
        }
      }
    }
    return true;
  });

  constructor() {
    // Emit allMandatoryChecklistCompleted whenever the computed value changes
    effect(() => {
      const done = this.allMandatoryCompleted();
      this.allMandatoryChecklistCompleted.emit(done);
    });
  }

/*   ngOnInit() {
    // Set this step as active in the wizard
    this.professionalsWizardService.setStep(2);
    this.loadAttachmentsForTipologia();
  } */

  ngOnInit() {
    const profId = this.getCurrentProfessionalId();
    if (profId) {
      this.loadSolCode(profId);
    }

    // ── Review/backoffice mode: load VRF status first (if needed), then load data ──
    if (this.professionalIdInput()) {
      if (this.shouldShowVRFQuestionnaire() && profId) {
        this.professionalsService.getVRFQuestionnaire(profId).subscribe({
          next: (vrfRes) => {
            this.vrfStatus.set(vrfRes.status);
            this.loadAttachmentsForTipologia();
          },
          error: () => {
            this.vrfStatus.set(null);
            this.loadAttachmentsForTipologia();
          }
        });
      } else {
        this.loadAttachmentsForTipologia();
      }
      return;
    }

    // Check for NK- route param (NK_BODYSHOP onboarding directly to attachments page)
    const routeId = this.route.snapshot.paramMap.get('id');
    if (routeId?.startsWith('NK-') &&
        (!this.professionalsWizardService.isNetworkOnboarding() ||
         this.professionalsWizardService.networkBodyShopStatus() === null)) {
      // Wizard not yet initialised (or prepareNKStepVisibility ran but status not yet known) — init from API
      this.professionalsService.getNetworkBodyShopById(routeId).subscribe({
        next: (res) => {
          if (res.status && res.data) {
            const subType = (res.data.bodyShopType ?? 'STANDARD') as import('../../../services/professionals-data.service').TipologiaCarrozzeria;
            this.professionalsWizardService.initNetworkOnboarding(routeId, res.data.networkId ?? '', subType, res.data.status ?? '');
            this.professionalsWizardService.markStepCompleted(1, true); // specific-data must have been completed to reach here
          }
          this.professionalsWizardService.setStep(2);
          this.loadAttachmentsOnlyForNK();
        },
        error: () => {
          this.professionalsWizardService.setStep(2);
          this.loadAttachmentsOnlyForNK();
        },
      });
      return;
    }

    // ── NK_BODYSHOP onboarding: skip registration-step, VRF and step-status APIs ──
    // Just load attachments (same checklist APIs as BODYSHOP standard) and set wizard step.
    if (this.professionalsWizardService.isNetworkOnboarding()) {
      // Step 1 (Dati Specifici) deve essere completato per arrivare qui;
      // lo ripetiamo esplicitamente come safety-net nel caso il singleton
      // abbia perso lo stato in-memory (e.g. reload della pagina).
      this.professionalsWizardService.markStepCompleted(1, true);
      this.professionalsWizardService.setStep(2);
      this.loadAttachmentsOnlyForNK();
      return;
    }
    // ── end NK_BODYSHOP bypass ──

    // Load current registration step from backend (needed for read-only mode detection)
    if (profId) {
      this.professionalsWizardService.loadCurrentStep(profId);
      
      // Load both current step AND history to detect modification requests
      forkJoin({
        currentStep: this.registrationStepService.getCurrentStep(profId),
        history: this.registrationStepService.getStepHistory(profId)
      }).subscribe({
        next: ({ currentStep, history }) => {
          this.currentRegistrationStep.set(currentStep);
          this.stepHistory.set(history || []);
          console.log('📋 [Questionnaire Attachments] Current registration step loaded:', currentStep);
          console.log('📋 [Questionnaire Attachments] Step history loaded:', history?.length || 0, 'steps');
          
          // Check if there's a modification request in history
          if (currentStep.step === 'DATA_INPUT' && 
              currentStep.status === RegistrationStatus.USER_INPUT) {
            const hasModification = history?.some(s => 
              s.step === 'VERIFICATION' && 
              s.status === RegistrationStatus.MODIFICATION_REQUESTED
            );
            
            if (hasModification) {
              console.log('⚠️ [Questionnaire Attachments] Modification request detected!');
              this.liveRegionMessage.set('Richiesta di modifica presente. Si prega di leggere i commenti del revisore.');
            }
          }
        },
        error: (err) => {
          console.error('❌ Error loading registration step or history:', err);
        }
      });
    }
    
    // 1. Set the UI to step 2
    this.professionalsWizardService.setStep(2);
    
    // 2. Load VRF status FIRST (se richiesto), poi carica gli altri dati
    if (profId && this.shouldShowVRFQuestionnaire()) {
      this.professionalsService.getVRFQuestionnaire(profId).subscribe({
        next: (vrfRes) => {
          this.vrfStatus.set(vrfRes.status);
          console.log(`📋 VRF status loaded: ${vrfRes.status}`);
          // Ora carica gli altri dati
          this.loadAttachmentsForTipologia();
        },
        error: (err) => {
          console.warn('VRF non trovato o errore:', err);
          this.vrfStatus.set(null);
          // Carica comunque gli altri dati
          this.loadAttachmentsForTipologia();
        }
      });
    } else {
      // VRF non richiesto, carica subito
      this.loadAttachmentsForTipologia();
    }

    // 3. Load completion status for all previous steps
    if (profId) {
      console.log('🔍 [Questionnaire Attachments] Loading completion status for previous steps...');
      
      // 3a. Load step 0 (general data) completion status using wizard service
      this.professionalsWizardService.preloadGeneralDataStatus(profId);

      // 3b. Load step 1 (specific data) completion status using wizard service
      const tipologia = this.tipologiaFiduciario ?? '';
      if (tipologia) {
        this.professionalsWizardService.preloadSpecificDataStatus(profId, tipologia);
      }

      // Lo stato dello step 2 viene calcolato dopo il caricamento di questionari,
      // VRF e checklist in loadAttachmentsForTipologia(). L'endpoint questionnaire-step
      // puo' restituire PENDING mentre gli item sono gia' presenti e non deve sovrascrivere
      // il risultato locale con un valore transitorio.
    }
  }

  /**
   * NK_BODYSHOP: carica solo gli allegati (checklist), senza questionari né VRF.
   * Marca lo step 2 completato basandosi unicamente sul completamento degli allegati obbligatori.
   */
  private loadAttachmentsOnlyForNK() {
    const profId = this.getCurrentProfessionalId();
    if (!profId) {
      console.warn('[NK] Professional ID non trovato in sessione');
      return;
    }

    // Usa la cache del wizard service (impostata da initNetworkOnboarding a 'NK_BODYSHOP')
    // invece di chiamare getProfessionalGeneralData(NK-...) che punta all'endpoint /professionals
    // e potrebbe non avere un record per gli ID NK-, restituendo null e forzando il fallback a 'BODYSHOP'.
    const cachedType = this.professionalsWizardService.cachedGeneralData()?.professionalType;
    // NK_BODYSHOP usa la stessa config checklist di BODYSHOP
    const rawType = cachedType ?? 'NK_BODYSHOP';
    const trusteeTypeResolved = rawType === 'NK_BODYSHOP' ? 'BODYSHOP' : rawType;
    console.log(`📋 [NK] Using trusteeType: ${trusteeTypeResolved} (raw: ${rawType})`);

    of(trusteeTypeResolved)
      .subscribe((trusteeType) => {

        this.questionnaireService
          .getFileChecklistStatusByProfessionalAndTrusteeType(profId, trusteeType)
          .pipe(catchError((err) => {
            console.error('❌ [NK] Error loading checklists:', err);
            return of([]);
          }))
          .subscribe((checklists) => {
            const checklistsData = checklists as any[];
            const firstChecklistName =
              Array.isArray(checklistsData) && checklistsData.length > 0
                ? checklistsData[0]?.name || 'Checklist'
                : 'Checklist';
            this.checklistSectionName.set(firstChecklistName);

            // Solo la sezione checklist, nessuna sezione questionari
            const checklistSection: AttachmentSection = {
              id: 'checklist',
              title: `Checklist ${this.tipologiaLabel()}`,
              items: this.mapChecklistItems(checklistsData),
            };
            this.sections.set([checklistSection]);

            // Completamento basato solo sugli allegati
            setTimeout(() => {
              const completed = checklistsData.length > 0 && this.areChecklistsCompleted();
              console.log(`📋 [NK] Allegati completati: ${completed}`);
              this.professionalsWizardService.markStepCompleted(2, completed);
            }, 0);
          });
      });
  }

  private loadAttachmentsForTipologia() {
    const profId = this.getCurrentProfessionalId();

    if (!profId) {
      console.warn('Professional ID non trovato in sessione');
      return;
    }

    // Prima ottieni la tipologia dal backend
    this.professionalsService.getProfessionalGeneralData(profId)
      .pipe(
        catchError((error) => {
          console.error('❌ [Questionnaire Attachments] Error loading general data:', error);
          return of(null);
        })
      )
      .subscribe((response) => {
        const data = (response as any)?.data ?? response;
        if (!data || !data.professionalType) {
          console.warn('⚠️ [Questionnaire Attachments] professionalType non disponibile nella risposta BE');
          return;
        }

        this.businessNameFromApi.set(data.companyData?.businessName || data.businessName || '');
        this.professionalTypeFromApi.set(data.professionalType);
        const trusteeType = data.professionalType;

        // Ora carica questionari e checklist con la tipologia recuperata
        forkJoin({
          // Stato Questionari (SURVEY)
          questionnaires: this.questionnaireService
            .listQuestionnairesByProfessionalAndTrusteeType(profId, trusteeType)
            .pipe(catchError((err) => {
              console.error('❌ Error loading questionnaires:', err);
              return of([]);
            })),

          // NUOVO: Stato Checklist (FILE_CHECKLIST + Risposte già caricate)
          checklists: this.questionnaireService
            .getFileChecklistStatusByProfessionalAndTrusteeType(profId, trusteeType)
            .pipe(catchError((err) => {
              console.error('❌ Error loading checklists:', err);
              return of([]);
            })),
        }).subscribe(({ questionnaires, checklists }) => {
          const checklistsData = checklists as any[];
          const firstChecklistName =
            Array.isArray(checklistsData) && checklistsData.length > 0
              ? checklistsData[0]?.name || 'Checklist'
              : 'Checklist';

          this.checklistSectionName.set(firstChecklistName);

          const questionariSection: AttachmentSection = {
            id: 'questionari',
            title: `Questionari ${this.tipologiaLabel()}`,
            items: this.mapQuestionnaireItems(questionnaires),
          };

          const checklistSection: AttachmentSection = {
            id: 'checklist',
            title: `Checklist ${this.tipologiaLabel()}`,
            items: this.mapChecklistItems(checklists as any[]),
          };

          this.sections.set([questionariSection, checklistSection]);

          // NOTE: do NOT rename to 'Allegati' here even when there are no questionnaires.
          // 'Allegati' is used exclusively in the NK_BODYSHOP flow (loadAttachmentsOnlyForNK).
          // In the standard flow the label stays 'Questionari e Allegati' regardless.

          // Dopo aver caricato i dati, verifica se lo step dovrebbe essere completato
          // Usa setTimeout per permettere ai computed di aggiornarsi
          setTimeout(() => {
            const localStepCompleted = this.shouldStepBeCompleted();
            console.log(`📋 Local step completion check: ${localStepCompleted}`);
            console.log(`📋 - Questionari completati: ${this.areQuestionnairesCompleted()}`);
            console.log(`📋 - Checklist completate: ${this.areChecklistsCompleted()}`);
            console.log(`📋 - Checklist presenti: ${this.checklistItems().length}`);
            
            // La verifica locale è la fonte di verità per la UI: aggiorna anche a false,
            // evitando di mantenere un completamento precedente del singleton/sessione.
            console.log(`📋 Marking step 2 as ${localStepCompleted ? 'completed' : 'not completed'} based on local data`);
            this.professionalsWizardService.markStepCompleted(2, localStepCompleted);
          }, 0);
        });
      });
  }

  private mapQuestionnaireItems(questionnaires: any[]): AttachmentItem[] {
    const mappedQuestionnaires = (questionnaires || []).map((item: any, index: number) => ({
      id: item.questionnaireId || `error-no-id-${index}`,
      title: item.name || `Questionario ${index + 1}`,

      isUploaded: item.status === "FILLED", // Usa il nuovo campo status invece di answerId
      answerId: item.answerId ?? null, // Usa null invece di undefined per coerenza con l'interfaccia
      instanceId: item.instanceId ?? null, // Usa null

      fileName: item.name || `questionario_${index + 1}.pdf`,
      fileSize: 'N/A', // AGGIUNTO: era obbligatorio nell'interfaccia
      uploadDate: item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('it-IT') : '',
isMandatory: item.isMandatory ?? false, // AGGIUNTO: se il BE fornisce questa info, altrimenti default a false

      templateUrl: item.templateFile ?? null, // Supporta null grazie alla modifica all'interfaccia
    }));

    // Aggiungi il VRF come primo questionario se deve essere mostrato
    if (this.shouldShowVRFQuestionnaire()) {
      const vrfItem: AttachmentItem = {
        id: 'VRF_QUESTIONNAIRE', // ID speciale per identificare il VRF
        title: 'Questionari di valutazione dei rischi rispetto agli standard AXA e informazioni per la predisposizione del contratto',
        fileName: 'vrf-questionnaire.pdf',
        fileSize: 'N/A',
        uploadDate: null,
        isMandatory: true,
        isUploaded: this.vrfStatus() === 'FILLED', // Usa il signal invece del TODO
        templateUrl: null,
      };
      
      return [vrfItem, ...mappedQuestionnaires];
    }
    
    return mappedQuestionnaires;
  }

  private mapChecklistItems(checklists: FileChecklistStatusDTO[]): AttachmentItem[] {
    return (checklists || []).map((item) => {
      // Gestione estensioni dal codice del collega
      const extensionsRaw = this.parseAllowedExtensions(item.allowedExtensions);

      return {
        // Dati identificativi dal tuo nuovo servizio (BE status)
        id: item.requestId,
        answerId: item.answerId,
        title: item.requestText || 'Documento',

        // Stato caricamento reale dal DB
        isUploaded: item.isUploaded,
        fileName: item.fileName || `${item.requestText}.pdf`,
        fileSystemPath: item.fileSystemPath || null, // URL S3 del file caricato

        // Campi aggiunti dal collega (per logica UI)
        allowedExtensionsRaw: extensionsRaw,
        allowedExtensions: extensionsRaw.map((ext) => ext.replace('.', '').toUpperCase()),
        hasExpirationDate: !!item.hasExpirationDate,
        expirationDate: item.expirationDate ? new Date(item.expirationDate) : null,
        pendingFile: null, // Inizialmente nullo

        // Campi standard AttachmentItem
        fileSize: '',
        uploadDate: item.expirationDate
          ? new Date(item.expirationDate).toLocaleDateString('it-IT')
          : '',
        isMandatory: item.isMandatory ?? false,
        // Se il BE ha caricato un template, usiamo un sentinel per attivare il bottone;
        // il download avviene via API (downloadChecklistTemplate) non via URL diretto
        templateUrl: item.isTemplateFilePresent ? '__checklist_template__' : null,
        isChecklistItem: true,
      };
    });
  }

  private parseAllowedExtensions(allowedExtensions: string | null | undefined): string[] {
    if (!allowedExtensions || typeof allowedExtensions !== 'string') {
      return [];
    }

    // Supporta sia ';' che ',' come separatori (il BE può usare entrambi)
    return allowedExtensions
      .split(/[;,]/)
      .map((ext) => ext.trim())
      .filter(Boolean)
      .map((ext) => {
        const normalized = ext.startsWith('.') ? ext : `.${ext}`;
        return normalized.toLowerCase();
      });
  }

  private normalizeIsoDateOnly(value: string | null | undefined): string | null {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;

    const datePart = trimmed.includes('T') ? trimmed.split('T')[0] : trimmed;
    return this.isIsoDateOnly(datePart) ? datePart : null;
  }

  private isIsoDateOnly(value: string | null | undefined): boolean {
    if (!value || typeof value !== 'string') return false;
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!isoDateRegex.test(value)) return false;

    const [yearStr, monthStr, dayStr] = value.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);

    const candidate = new Date(Date.UTC(year, month - 1, day));
    return (
      candidate.getUTCFullYear() === year &&
      candidate.getUTCMonth() === month - 1 &&
      candidate.getUTCDate() === day
    );
  }

  getAcceptAttribute(item: AttachmentItem): string {
    return item.allowedExtensionsRaw && item.allowedExtensionsRaw.length > 0
      ? item.allowedExtensionsRaw.join(',')
      : '';
  }

  getAllowedExtensionsLabel(item: AttachmentItem | null | undefined): string {
    if (!item?.allowedExtensions || item.allowedExtensions.length === 0) {
      return 'Tutti i formati';
    }

    return item.allowedExtensions.join(', ');
  }

  canUploadCurrentChecklistItem(): boolean {
    const currentItem = this.activeChecklistUploadItem();
    if (!currentItem) {
      return false;
    }

    return this.canSaveChecklistItem(currentItem);
  }

  getUploadDialogValidationMessage(): string {
    const currentItem = this.activeChecklistUploadItem();
    if (!currentItem) {
      return '';
    }

    if (!currentItem.pendingFile) {
      return 'Seleziona un file prima di procedere con il caricamento.';
    }

    if (currentItem.hasExpirationDate && !currentItem.expirationDate) {
      return 'La scadenza documento è obbligatoria.';
    }

    return '';
  }

  shouldShowUploadDialogError(): boolean {
    if (!this.uploadValidationAttempted()) {
      return false;
    }

    return !!this.getUploadDialogValidationMessage();
  }

  canSaveChecklistItem(item: AttachmentItem): boolean {
    // Deve esserci un file selezionato
    if (!item.pendingFile) return false;

    if (item.hasExpirationDate) {
      // Se non c'è una data, non può salvare
      if (!item.expirationDate) return false;

      // Se è un oggetto Date, lo trasformiamo in stringa YYYY-MM-DD per il validatore
      const dateStr =
        item.expirationDate instanceof Date
          ? item.expirationDate.toISOString().split('T')[0]
          : item.expirationDate;

      return this.isIsoDateOnly(dateStr);
    }

    return true;
  }

  getExpirationDateAsDate(item: AttachmentViewItem): Date | null {
    if (!item.expirationDate) return null;
    const d = new Date(item.expirationDate + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
  }

  onExpirationDateChange(value: Date | null, sectionId: string, itemId: string) {
    this.sections.update((sections) =>
      sections.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          items: section.items.map((item) =>
            item.id === itemId ? { ...item, expirationDate: value } : item,
          ),
        };
      }),
    );
    this.dirtyExpirationItems.update((s) => new Set(s).add(itemId));
  }

  saveItemExpirationDate(item: AttachmentViewItem): void {
    if (!item.expirationDate || !item.answerId) return;
    const dateStr =
      item.expirationDate instanceof Date
        ? item.expirationDate.toISOString().split('T')[0]
        : null;
    if (!dateStr || !this.isIsoDateOnly(dateStr)) return;

    this.savingExpirationFor.set(item.id);
    this.questionnaireService.updateChecklistItemExpiration(item.answerId, dateStr).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Data aggiornata',
          detail: 'La data di scadenza è stata salvata correttamente.',
          life: 3000,
        });
        this.savingExpirationFor.set(null);
        this.dirtyExpirationItems.update((s) => { const n = new Set(s); n.delete(item.id); return n; });
      },
      error: (err) => {
        console.error('Errore aggiornamento data di scadenza:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile aggiornare la data di scadenza. Riprova.',
          life: 4000,
        });
        this.savingExpirationFor.set(null);
      },
    });
  }

  saveChecklistItem(sectionId: string, itemId: string) {
    const targetSection = this.sections().find((s) => s.id === sectionId);
    const targetItem = targetSection?.items.find((i) => i.id === itemId);

    // Controllo file pendente
    if (!targetItem?.pendingFile) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Seleziona prima un file valido',
        life: 4000,
      });
      return;
    }

    // Validazione data (YYYY-MM-DD)
    const dateStr =
      targetItem.expirationDate instanceof Date
        ? targetItem.expirationDate.toISOString().split('T')[0]
        : null;

    if (targetItem.hasExpirationDate && (!dateStr || !this.isIsoDateOnly(dateStr))) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'La data di scadenza è obbligatoria e deve essere valida',
        life: 4000,
      });
      return;
    }

    const profId = this.getCurrentProfessionalId();
    if (!profId) return;

    this.isSubmitting.set(true);

    // NUOVO FLUSSO: Upload diretto in posizione finale + salvataggio DB in un'unica chiamata
    this.questionnaireService
      .uploadFileToS3(targetItem.pendingFile, profId, itemId, dateStr)
      .then(() => {
        this.isSubmitting.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: `Documento "${targetItem.title}" salvato con successo`,
          life: 4000,
        });

        if (this.showChecklistUploadDialog()) {
          this.closeChecklistUploadDialog();
        }

        // REFRESH: Aggiorna la lista per mostrare il file caricato
        // Per il flusso NK usa il metodo dedicato (tipo BODYSHOP normalizzato);
        // loadAttachmentsForTipologia chiamerebbe getProfessionalGeneralData(NK-...)
        // che restituisce NK_BODYSHOP, poi getFileChecklistStatus con tipo sbagliato → lista vuota
        if (this.professionalsWizardService.isNetworkOnboarding()) {
          this.loadAttachmentsOnlyForNK();
        } else {
          this.loadAttachmentsForTipologia();
        }
      })
      .catch(() => {
        this.isSubmitting.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il caricamento del file',
          life: 5000,
        });
      });
  }

  removePendingChecklistFile(sectionId: string, itemId: string, uploader?: FileUpload) {
    uploader?.clear();

    this.sections.update((sections) =>
      sections.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          items: section.items.map((item) => {
            if (item.id !== itemId) return item;
            return {
              ...item,
              pendingFile: null,
              expirationDate: null,
              fileSize: '',
            };
          }),
        };
      }),
    );
  }

  openChecklistUploadDialog(sectionId: string, itemId: string): void {
    this.clearPendingChecklistState(sectionId, itemId);
    this.uploadTargetSectionId.set(sectionId);
    this.uploadTargetItemId.set(itemId);
    this.uploadValidationAttempted.set(false);
    this.uploadInlineErrorMessage.set('');
    this.showChecklistUploadDialog.set(true);
  }

  closeChecklistUploadDialog(): void {
    const activeItem = this.activeChecklistUploadItem();
    if (activeItem) {
      this.clearPendingChecklistState(activeItem.sectionId, activeItem.id);
    }

    this.showChecklistUploadDialog.set(false);
    this.uploadTargetSectionId.set(null);
    this.uploadTargetItemId.set(null);
    this.uploadValidationAttempted.set(false);
    this.uploadInlineErrorMessage.set('');
  }

  onDialogFileSelect(event: any): void {
    const activeItem = this.activeChecklistUploadItem();
    if (!activeItem) {
      return;
    }

    this.uploadInlineErrorMessage.set('');
    this.onFileSelect(event, activeItem.sectionId, activeItem.id);
  }

  onDialogFileRemoved(uploader: FileUpload): void {
    const activeItem = this.activeChecklistUploadItem();
    if (!activeItem) {
      return;
    }

    this.removePendingChecklistFile(activeItem.sectionId, activeItem.id, uploader);
  }

  onDialogUpload(event: any): void {
    const activeItem = this.activeChecklistUploadItem();
    if (!activeItem) {
      return;
    }

    this.uploadValidationAttempted.set(true);

    const file = event.files?.[0];
    if (!activeItem.pendingFile && file) {
      this.onFileSelect({ files: [file] }, activeItem.sectionId, activeItem.id);
    }

    const refreshedItem = this.getChecklistItem(activeItem.sectionId, activeItem.id);
    if (!refreshedItem || !this.canSaveChecklistItem(refreshedItem)) {
      const inlineMessage = this.getUploadDialogValidationMessage();
      this.uploadInlineErrorMessage.set(inlineMessage);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: inlineMessage ||
          (refreshedItem?.hasExpirationDate
            ? 'Seleziona un file valido e inserisci la scadenza documento'
            : 'Seleziona prima un file valido'),
        life: 4000,
      });
      return;
    }

    this.uploadInlineErrorMessage.set('');

    this.saveChecklistItem(activeItem.sectionId, activeItem.id);
  }

  private getChecklistItem(sectionId: string, itemId: string): AttachmentItem | undefined {
    const section = this.sections().find((entry) => entry.id === sectionId);
    return section?.items.find((entry) => entry.id === itemId);
  }

  private clearPendingChecklistState(sectionId: string, itemId: string): void {
    this.sections.update((sections) =>
      sections.map((section) => {
        if (section.id !== sectionId) return section;

        return {
          ...section,
          items: section.items.map((item) => {
            if (item.id !== itemId) return item;

            return {
              ...item,
              pendingFile: null,
              expirationDate: null,
              fileSize: '',
            };
          }),
        };
      }),
    );
  }

  downloadTemplate(item: AttachmentItem) {
    if (!item.templateUrl) return;

    // Checklist item: scarica il template via API usando l'ID dell'item
    if (item.isChecklistItem) {
      this.questionnaireService.downloadChecklistTemplate(item.id).subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = item.title || 'template';
          a.click();
          URL.revokeObjectURL(url);
        },
        error: () => this.messageService.add({
          severity: 'error',
          summary: 'Errore download',
          detail: 'Impossibile scaricare il template. Riprova tra poco.',
          life: 4000,
        }),
      });
      return;
    }

    // Questionario: URL diretto
    window.open(item.templateUrl, '_blank');
  }

  compileQuestionnaire(item: AttachmentItem) {
    if (!item?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Questionario non disponibile',
        life: 4000,
      });
      return;
    }

    // Se è il VRF, naviga alla pagina dedicata (passa professional ID se in review mode)
    if (item.id === 'VRF_QUESTIONNAIRE') {
      const professionalId = this.professionalIdInput();
      if (professionalId) {
        // Review mode: passa l'ID nella route
        this.router.navigate(['/axa/vrf-questionnaire', professionalId]).then(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      } else {
        // Normal mode: naviga senza ID
        this.router.navigate(['/axa/vrf-questionnaire']).then(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      }
      return;
    }

    // Passa professionalId e trusteeType come query params se siamo in review mode (backoffice)
    const queryParams: Record<string, string> = {};
    const reviewProfessionalId = this.professionalIdInput();
    
    if (reviewProfessionalId && this.tipologiaFiduciario) {
      queryParams['professionalId'] = reviewProfessionalId;
      queryParams['trusteeType'] = this.tipologiaFiduciario;
      console.log('👁️ [Review Mode] Navigating with params:', queryParams);
    }
    
    this.router.navigate(['/questionnaires', item.id, 'run'], {
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined
    }).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  editQuestionnaire(item: AttachmentViewItem) {
    if (!item.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Questionario non disponibile per la modifica',
        life: 4000,
      });
      return;
    }

    // Passa professionalId e trusteeType come query params se siamo in review mode (backoffice)
    const queryParams: Record<string, string> = {};
    const reviewProfessionalId = this.professionalIdInput();
    
    if (reviewProfessionalId && this.tipologiaFiduciario) {
      queryParams['professionalId'] = reviewProfessionalId;
      queryParams['trusteeType'] = this.tipologiaFiduciario;
      console.log('👁️ [Review Mode] Navigating with params:', queryParams);
    }

    this.router.navigate(['/questionnaires', item.id, 'run'], {
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined
    }).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  viewQuestionnaire(item: AttachmentViewItem) {
    if (!item.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Questionario non disponibile',
        life: 4000,
      });
      return;
    }

    // Se è il VRF, naviga alla pagina dedicata (passa professional ID se in review mode)
    if (item.id === 'VRF_QUESTIONNAIRE') {
      const professionalId = this.professionalIdInput();
      if (professionalId) {
        // Review mode: passa l'ID nella route
        this.router.navigate(['/axa/vrf-questionnaire', professionalId]).then(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      } else {
        // Normal mode: naviga senza ID
        this.router.navigate(['/axa/vrf-questionnaire']).then(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      }
      return;
    }

    // Passa professionalId e trusteeType come query params se siamo in review mode (backoffice)
    const queryParams: Record<string, string> = {};
    const reviewProfessionalId = this.professionalIdInput();
    
    if (reviewProfessionalId && this.tipologiaFiduciario) {
      queryParams['professionalId'] = reviewProfessionalId;
      queryParams['trusteeType'] = this.tipologiaFiduciario;
      // alertMap non viene più passato: questionnaire-run carica gli alert via API
    }
    
    this.router.navigate(['/questionnaires', item.id, 'run'], {
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined
    }).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  downloadQuestionnairePdf(item: AttachmentViewItem) {
    const pdfInstanceId = item.instanceId ?? item.answerId ?? null;

    if (!item.id || !pdfInstanceId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile scaricare il PDF: questionario non compilato',
        life: 4000,
      });
      return;
    }

    const professionalId = this.getCurrentProfessionalId();
    if (!professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID fiduciario non disponibile',
        life: 4000,
      });
      return;
    }

    this.getQuestionnairePdfHeaderData(professionalId).subscribe({
      next: (headerData) => this.questionnaireService.downloadPdf(item.id, pdfInstanceId, headerData).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const sanitizedFileName = addSolCodeToFileName(
          this.sanitizeFileName(item.title || 'questionario') + '.pdf',
          headerData.solCode,
        );
        
        const a = document.createElement('a');
        a.href = url;
        a.download = sanitizedFileName;
        a.click();
        
        window.URL.revokeObjectURL(url);
        
        this.messageService.add({
          severity: 'success',
          summary: 'Download completato',
          detail: `PDF "${item.title}" scaricato con successo`,
          life: 3000,
        });
      },
      error: (err) => {
        console.error('Errore download PDF:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile scaricare il PDF',
          life: 4000,
        });
      },
      }),
      error: (err) => {
        console.error('Errore caricamento dati intestazione PDF:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile recuperare i dati del fiduciario',
          life: 4000,
        });
      },
    });
  }

  private getQuestionnairePdfHeaderData(professionalId: string) {
    return forkJoin({
      generalData: this.professionalsService.getProfessionalGeneralData(professionalId).pipe(
        catchError(() => of(null)),
      ),
      operationalDetails: this.professionalOperationalDetailsService.getOperationalDetails(professionalId).pipe(
        catchError(() => of(null)),
      ),
    }).pipe(
      map(({ generalData, operationalDetails }): QuestionnairePdfHeaderData => {
        const general = (generalData as any)?.data ?? generalData ?? {};
        const companyData = general.companyData ?? {};
        const operational = (operationalDetails as any)?.data ?? operationalDetails ?? {};
        const otherFields = operational.otherFields ?? {};
        const legalOrPhysical = String(companyData.legalOrPhysical ?? general.legalOrPhysical ?? '')
          .trim()
          .toUpperCase();
        const isPhysicalPerson = legalOrPhysical === 'FISICA'
          || legalOrPhysical === 'PHYSICAL'
          || legalOrPhysical === 'PHYSICAL_PERSON';
        const identificationCode = isPhysicalPerson
          ? companyData.fiscalCode || general.fiscalCode || '-'
          : companyData.vatNumber || general.vatNumber || general.bankDetails?.vatNumber || '-';

        return {
          businessName: companyData.businessName || general.businessName || '-',
          professionalType: general.professionalType || this.tipologiaFiduciario || '',
          solCode: otherFields.solCode || professionalId,
          vatNumber: identificationCode,
        };
      }),
    );
  }

  async onFileSelect(event: any, sectionId: string, itemId: string) {
    const file = event.files[0];
    if (file) {
      // Get the item title for announcement
      let itemTitle = '';
      let itemAllowedExtensions: string[] = [];
      let hasExpirationDate = false;
      this.sections().forEach((section) => {
        const item = section.items.find((i) => i.id === itemId);
        if (item) {
          itemTitle = item.title;
          itemAllowedExtensions = item.allowedExtensionsRaw || [];
          hasExpirationDate = !!item.hasExpirationDate;
        }
      });

      const fileExtension = file.name.includes('.')
        ? `.${file.name.split('.').pop()?.toLowerCase().trim()}`
        : '';

      const isAllowed =
        itemAllowedExtensions.length === 0 || itemAllowedExtensions.includes(fileExtension);

      if (!isAllowed) {
        this.messageService.add({
          severity: 'error',
          summary: 'Formato non consentito',
          detail: `Formato non consentito per "${itemTitle}". Formati ammessi: ${itemAllowedExtensions.join(', ')}`,
          life: 5000,
        });
        return;
      }

      // 🔒 Validazione completa del file con FileValidationService
      // Include: size, MIME type, extension, magic numbers (file signature)
      // Supporta: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, JPEG, PNG
      const validation = await this.fileValidationService.validateFileChecklist(file);
      if (!validation.valid) {
        this.messageService.add({
          severity: 'error',
          summary: '🔒 Validazione File Fallita',
          detail: validation.error || 'Il file non ha superato i controlli di sicurezza',
          life: 6000,
        });

        // Log dettagli per debugging (solo in sviluppo)
        if (validation.details) {
          console.warn('File validation failed:', validation.details);
        }

        return;
      }

      this.sections.update((sections) =>
        sections.map((section) => {
          if (section.id === sectionId) {
            return {
              ...section,
              items: section.items.map((item) => {
                if (item.id === itemId) {
                  return {
                    ...item,
                    pendingFile: file,
                    fileSize: this.formatFileSize(file.size),
                  };
                }
                return item;
              }),
            };
          }
          return section;
        }),
      );

      // Announce success
      this.liveRegionMessage.set(`File ${itemTitle} selezionato correttamente`);
      setTimeout(() => this.liveRegionMessage.set(''), 3000);

      // Show success message
      this.messageService.add({
        severity: 'success',
        summary: 'File selezionato',
        detail: hasExpirationDate
          ? `File "${itemTitle}" selezionato. Inserisci la data di scadenza e premi Carica`
          : `File "${itemTitle}" selezionato. Premi Carica per confermare`,
        life: 4000,
      });
    }
  }

  onFileError(event: any, item: AttachmentItem) {
    console.error('File upload error:', event);

    let errorMsg = 'Errore durante il caricamento del file';
    if (event.error) {
      // Check if file size is too large
      if (event.files && event.files[0] && event.files[0].size > 10000000) {
        errorMsg = 'Il file supera la dimensione massima di 10 MB';
      }
    }

    // Announce error
    this.liveRegionMessage.set(`Errore: ${errorMsg} per ${item.title}`);
    setTimeout(() => this.liveRegionMessage.set(''), 3000);

    // Show error toast
    this.messageService.add({
      severity: 'error',
      summary: 'Errore',
      detail: `${errorMsg}: "${item.title}"`,
      life: 5000,
    });
  }

  downloadUploadedFile(item: AttachmentItem) {
    if (!item.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Identificativo file non disponibile',
        life: 4000,
      });
      return;
    }

    const professionalId = this.getCurrentProfessionalId();
    if (!professionalId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID fiduciario non disponibile',
        life: 4000,
      });
      return;
    }

    // Scarica il file tramite l'API del backend
    this.questionnaireService.downloadFileChecklist(professionalId, item.id).subscribe({
      next: (blob) => {
        // Crea un URL locale temporaneo per il blob
        const url = window.URL.createObjectURL(blob);
        
        // Sanitizza il nome file per prevenire XSS
        const sanitizedFileName = addSolCodeToFileName(
          this.sanitizeFileName(item.fileName || 'documento'),
          this.solCode(),
        );
        
        // Crea un link di download (non aggiunto al DOM per sicurezza)
        const a = document.createElement('a');
        a.href = url;
        a.download = sanitizedFileName;
        a.click(); // Modern browsers support click() without appendChild
        
        // Pulisci
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Errore download file:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile scaricare il file',
          life: 4000,
        });
      },
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  formatSelectedFileName(fileName: string): string {
    if (!fileName) return '';

    const lastDotIndex = fileName.lastIndexOf('.');
    const hasExtension = lastDotIndex > 0;
    const extension = hasExtension ? fileName.substring(lastDotIndex) : '';
    const baseName = hasExtension ? fileName.substring(0, lastDotIndex) : fileName;

    if (baseName.length <= 20) {
      return `${baseName}${extension}`;
    }

    return `${baseName.substring(0, 20)}${extension}`;
  }

  saveDraft() {
    // Save current state as draft
    console.log('Saving draft...');
    const attachmentsData = {
      sections: this.sections(),
      lastUpdated: new Date().toISOString(),
    };
    // In production, save to service/API
    this.professionalsDataService.saveAttachmentsData(attachmentsData);

    // Save wizard state without marking as completed
    this.professionalsWizardService.saveState();

    // Announce success
    this.liveRegionMessage.set('Bozza salvata con successo');
    setTimeout(() => this.liveRegionMessage.set(''), 3000);

    // Show success toast
    this.messageService.add({
      severity: 'success',
      summary: 'Successo',
      detail: 'Bozza salvata con successo',
      life: 4000,
    });
  }

  submitRegistration() {
    if (this.allMandatoryCompleted()) {
      console.log('Submitting registration...');
      // In production, submit to API and change status to "In attesa di verifica"
      const finalData = this.professionalsDataService.getAllData();
      console.log('Final registration data:', finalData);

      // Mark step 2 (Questionari e Allegati) as completed
      this.professionalsWizardService.markStepCompleted(2, true);
      this.professionalsWizardService.saveState();

      // Announce success
      this.liveRegionMessage.set('Anagrafica inoltrata con successo');

      // Show success toast
      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Anagrafica inoltrata con successo',
        life: 4000,
      });

      // Navigate to confirmation or dashboard (step page per vedere lo stato)
      setTimeout(() => {
        this.router.navigate(['/axa/professionals-registration-progress']).then(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      }, 1500);
    }
  }

  /**
   * Navigate back to progression page from read-only mode
   */
  backToProgression(): void {
    console.log('[Questionnaire-Attachments] Returning to progression');
    this.router.navigate(['/axa/professionals-registration-progress']).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  private getCurrentProfessionalId(): string | null {
    // Priority 1: Use professionalIdInput if provided (from review page)
    const inputId = this.professionalIdInput();
    if (inputId) return inputId;

    // Priority 2: Route param :id (NK_BODYSHOP onboarding)
    const routeId = this.route.snapshot.paramMap.get('id');
    if (routeId) return routeId;

    // Priority 3: Network onboarding — body shop ID stored in wizard service
    if (this.professionalsWizardService.isNetworkOnboarding()) {
      return this.professionalsWizardService.networkOnboardingBodyShopId() || null;
    }

    // Priority 4: Recupera l'ID salvato durante il login (VARCHAR 20) — frontoffice user flow
    return sessionStorage.getItem('professional_id');
  }

  private sanitizeFileName(fileName: string): string {
    if (!fileName) {
      return 'documento';
    }
    
    let sanitized = fileName.replace(/\.\.\//g, '').replace(/\.\.\\/g, '');
    sanitized = sanitized.replace(/[^a-zA-Z0-9\s.\-_]/g, '_');
    
    if (sanitized.length > 255) {
      const ext = sanitized.substring(sanitized.lastIndexOf('.'));
      sanitized = sanitized.substring(0, 255 - ext.length) + ext;
    }
    
    return sanitized || 'documento';
  }

  private loadSolCode(professionalId: string): void {
    this.professionalOperationalDetailsService.getOperationalDetails(professionalId).subscribe({
      next: (response: any) => {
        const data = response?.data ?? response;
        this.solCode.set(data?.otherFields?.solCode ?? data?.solCode ?? null);
      },
      error: () => this.solCode.set(null),
    });
  }
}
